import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

import { useLazyGetSensorValuesQuery } from '../../../entities/equipments/sensorsValuesResource/redux/sensorValuesResource.api';
import { useGetAdaptersQuery } from '../../../entities/equipments/adaptersResource/redux/adaptersResource.api';
import { fetchSensorData } from './utils/fetchSensorData';
import isValidDate from '../../../shared/utils/isValidDate';
import { toTimestamp } from '../SensorChart/common';

// Преобразует дату в ISO-строку, проверяя валидность
const getCurrentDate = (date) => {
  if (!date || !isValidDate(date)) return null;

  return date.toISOString();
};

// Получает и подготавливает данные сенсоров для отображения графиков
function VehicleSensorCharts({ vehicleId, sensors, dateStart, dateFinish, onDataUpdate }) {
  const isoDateStartCurrent = getCurrentDate(dateStart);
  const isoDateFinishCurrent = getCurrentDate(dateFinish);

  const domain = useMemo(() => {
    if (!isoDateStartCurrent || !isoDateFinishCurrent) return undefined;
    return [toTimestamp(isoDateStartCurrent), toTimestamp(isoDateFinishCurrent)];
  }, [isoDateStartCurrent, isoDateFinishCurrent]);

  const emptyData = useMemo(() => {
    const start = isoDateStartCurrent || new Date().toISOString();
    const finish = isoDateFinishCurrent || new Date().toISOString();
    return [
      { createdDate: start, value: null },
      { createdDate: finish, value: null },
    ];
  }, [isoDateStartCurrent, isoDateFinishCurrent]);

  const [fetchSensorValues, { isFetching }] = useLazyGetSensorValuesQuery();

  const { data: adaptersData } = useGetAdaptersQuery({
      params: {
        'vehicleId.equals': vehicleId,
      }
    },
    { skip: !vehicleId });

  const thresholdsMap = useMemo(() => {
    const map = new Map();
    const adapter = adaptersData?.data?.[0];
    if (!adapter?.vehicleConfig) return map;

    adapter.vehicleConfig.forEach((item) => {
      const stId = item.sensorValueType?.sensorTypeId;
      if (!stId) return;

      map.set(stId, {
        lowWarning: item.lowWarningThresh,
        highWarning: item.highWarningThresh,
        lowError: item.lowErrorThresh,
        highError: item.highErrorThresh,
        warningEnabled: item.warningEnable,
        errorEnabled: item.errorEnable,
      });
    });

    return map;
  }, [adaptersData]);

  const [sensorDataMap, setSensorDataMap] = useState(new Map());
  const prevSensorsRef = useRef([]);
  const prevParamsRef = useRef({
    vehicleId,
    isoDateStartCurrent,
    isoDateFinishCurrent,
  });

  const isReady = !!(vehicleId && isoDateStartCurrent && isoDateFinishCurrent);

  useEffect(() => {
    if (!isReady) return;

    const prevSensors = prevSensorsRef.current;
    const prevIds = new Set(prevSensors.map((s) => s.sensorTypeId));
    const currentIds = new Set(sensors.map((s) => s.sensorTypeId));

    const added = sensors.filter((s) => !prevIds.has(s.sensorTypeId));
    const removed = prevSensors.filter((s) => !currentIds.has(s.sensorTypeId));

    if (removed.length) {
      setSensorDataMap((prev) => {
        const newMap = new Map(prev);
        removed.forEach((s) => newMap.delete(s.sensorTypeId));
        return newMap;
      });
    }

    const prevParams = prevParamsRef.current;
    const paramsChanged =
      prevParams.vehicleId !== vehicleId ||
      prevParams.isoDateStartCurrent !== isoDateStartCurrent ||
      prevParams.isoDateFinishCurrent !== isoDateFinishCurrent;

    const fetchTargets = paramsChanged ? sensors : added;

    if (!fetchTargets.length) {
      prevSensorsRef.current = sensors;
      prevParamsRef.current = { vehicleId, isoDateStartCurrent, isoDateFinishCurrent };
      return;
    }

    const fetchData = async () => {
      const result = await fetchSensorData({
        sensors: fetchTargets,
        vehicleId,
        isoDateStartCurrent,
        isoDateFinishCurrent,
        fetchSensorValues,
      });

      if (result.size) {
        setSensorDataMap((prev) => {
          const newMap = new Map(prev);
          result.forEach((value, key) => {
            if (!value || !value.length) {
              newMap.set(key, emptyData);
            } else {
              newMap.set(key, value);
            }
          });
          return newMap;
        });
      } else {
        const newMap = new Map();
        sensors.forEach((s) => {
          newMap.set(s.sensorTypeId, emptyData);
        });
        setSensorDataMap(newMap);
      }
    };

    fetchData();

    prevSensorsRef.current = sensors;
    prevParamsRef.current = { vehicleId, isoDateStartCurrent, isoDateFinishCurrent };
  }, [sensors, vehicleId, isoDateStartCurrent, isoDateFinishCurrent, isReady, fetchSensorValues]);

  useEffect(() => {
    if (onDataUpdate && sensorDataMap.size) {
      onDataUpdate({ sensorDataMap, thresholdsMap, domain });
    }
  }, [sensorDataMap, thresholdsMap, domain, onDataUpdate]);

  return (
    <Box>
      {isFetching && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

VehicleSensorCharts.propTypes = {
  vehicleId: PropTypes.number.isRequired,
  sensors: PropTypes.arrayOf(
    PropTypes.shape({
      sensorTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  ).isRequired,
  dateStart: PropTypes.instanceOf(Date),
  dateFinish: PropTypes.instanceOf(Date),
  onDataUpdate: PropTypes.func,
};

VehicleSensorCharts.defaultProps = {
  dateStart: null,
  dateFinish: null,
  onDataUpdate: null,
};

export default VehicleSensorCharts;
